import {Box} from '@mui/material';
import {t} from 'i18next';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {
  useDatasetDocListLimit,
  useDatasetDocViewLimit,
  useDocListLimit,
  useDocViewLimit,
  useLongSortLimit,
} from '../../../hooks/Projects/useProjects';
import {
  DocMaxLimitDestination,
  DocMaxLimitDestinationCode,
  SourceTypeCode,
} from '../../../shared/constants';
import ArrButton, {ArrButtonVariant} from '../../common/arr-button/ArrButton';
import ArrModal from '../../common/arr-modal/ArrModal';
import './ArrDocMaxLimitWarning.scss';
import ArrLoadingSpinner from '../arr-loading-spinner/ArrLoadingSpinner';
import {useQueryClient} from '@tanstack/react-query';
import {isEmpty} from '../../../utils/formatters';

// Types Definition
// ---------------------------
type ActionButton = {
  id: string | number;
  label: string;
  onClick: () => void;
  variant: ArrButtonVariant;
  dataTestId: string;
};

// --------------
interface ArrDocMaxLimitWarningProps {
  show: boolean;
  destination?: DocMaxLimitDestination;
  totalDocumentCount?: number;
  onContinue?: (totalDocumentToView: number) => void;
  onCancel?: () => void;
}
// --------------

// -----------------------------------
// ArrDocMaxLimitWarningFooter Component
// -----------------------------------
const ArrDocMaxLimitWarningFooter = ({
  actionButtons,
}: {
  actionButtons: ActionButton[];
}) => {
  return (
    <Box className="ArrDocMaxLimitWarning__footer">
      {actionButtons.map((actionButton) => (
        <ArrButton
          key={`actionBtn-${actionButton.label}`}
          variant={actionButton.variant}
          onClick={actionButton.onClick}
          dataTestId={`arr-doc-max-limit-warning-button-${actionButton.dataTestId}`}
        >
          {actionButton.label}
        </ArrButton>
      ))}
    </Box>
  );
};

// -----------------------------------
// useDocLimitConfig Hook
// -----------------------------------
const useDocLimitConfig = (
  destination: DocMaxLimitDestination | undefined,
  totalDocumentCount: number | undefined
) => {
  const [maxCount, setMaxCount] = useState<number | undefined>(undefined);
  const [isMaxLimit, setIsMaxLimit] = useState<boolean | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean | undefined>(undefined);
  const [totalDocumentToView, setTotalDocumentToView] = useState<
    number | undefined
  >(undefined);

  const {
    data: docViewLimit,
    refetch: docViewLimitRequest,
    isLoading: docViewLimitLoading,
  } = useDocViewLimit(false);

  const {
    data: docListLimit,
    refetch: docListLimitRequest,
    isLoading: docListLimitLoading,
  } = useDocListLimit(false);

  const {
    data: datasetDocViewLimit,
    refetch: datasetDocViewLimitRequest,
    isLoading: datasetDocViewLimitLoading,
  } = useDatasetDocViewLimit(false);

  const {
    data: datasetDocListLimit,
    refetch: datasetDocListLimitRequest,
    isLoading: datasetDocListLimitLoading,
  } = useDatasetDocListLimit(false);

  useEffect(() => {
    switch (destination) {
      case SourceTypeCode.DOCVIEW: {
        docViewLimitRequest();
        break;
      }
      case DocMaxLimitDestinationCode.DATASETDOCVIEW: {
        datasetDocViewLimitRequest();
        break;
      }
      case SourceTypeCode.DOCLIST: {
        docListLimitRequest();
        break;
      }
      case DocMaxLimitDestinationCode.DATASETDOCLIST: {
        datasetDocListLimitRequest();
        break;
      }
      default: {
        break;
      }
    }
  }, [
    datasetDocListLimitRequest,
    datasetDocViewLimitRequest,
    destination,
    docListLimitRequest,
    docViewLimitRequest,
  ]);

  useEffect(() => {
    setIsLoading(
      docViewLimitLoading ||
        docListLimitLoading ||
        datasetDocViewLimitLoading ||
        datasetDocListLimitLoading
    );
    if (
      !isEmpty(destination) &&
      !isEmpty(totalDocumentCount) &&
      !(
        docViewLimitLoading ||
        docListLimitLoading ||
        datasetDocViewLimitLoading ||
        datasetDocListLimitLoading
      ) &&
      !(
        isEmpty(docViewLimit) &&
        isEmpty(docListLimit) &&
        isEmpty(datasetDocViewLimit) &&
        isEmpty(datasetDocListLimit)
      )
    ) {
      let isMaxLimitExist = false;
      switch (destination) {
        case SourceTypeCode.DOCVIEW: {
          isMaxLimitExist =
            docViewLimit !== 0 &&
            totalDocumentCount !== 0 &&
            docViewLimit < (totalDocumentCount ?? 0);
          setMaxCount(docViewLimit);
          setIsMaxLimit(isMaxLimitExist);
          setTotalDocumentToView(
            isMaxLimitExist ? docViewLimit : totalDocumentCount
          );
          break;
        }
        case DocMaxLimitDestinationCode.DATASETDOCVIEW: {
          isMaxLimitExist =
            datasetDocViewLimit !== 0 &&
            totalDocumentCount !== 0 &&
            datasetDocViewLimit < (totalDocumentCount ?? 0);
          setMaxCount(datasetDocViewLimit);
          setIsMaxLimit(isMaxLimitExist);
          setTotalDocumentToView(
            isMaxLimitExist ? datasetDocViewLimit : totalDocumentCount
          );
          break;
        }
        case SourceTypeCode.DOCLIST: {
          isMaxLimitExist =
            docListLimit !== 0 &&
            totalDocumentCount !== 0 &&
            docListLimit < (totalDocumentCount ?? 0);
          setMaxCount(docListLimit);
          setIsMaxLimit(isMaxLimitExist);
          setTotalDocumentToView(
            isMaxLimitExist ? docListLimit : totalDocumentCount
          );
          break;
        }
        case DocMaxLimitDestinationCode.DATASETDOCLIST: {
          isMaxLimitExist =
            datasetDocListLimit !== 0 &&
            totalDocumentCount !== 0 &&
            datasetDocListLimit < (totalDocumentCount ?? 0);
          setMaxCount(datasetDocListLimit);
          setIsMaxLimit(isMaxLimitExist);
          setTotalDocumentToView(
            isMaxLimitExist ? datasetDocListLimit : totalDocumentCount
          );
          break;
        }
        default: {
          break;
        }
      }
    }
  }, [
    datasetDocListLimit,
    datasetDocViewLimit,
    datasetDocListLimitLoading,
    datasetDocViewLimitLoading,
    docViewLimit,
    docListLimit,
    docViewLimitLoading,
    docListLimitLoading,
    destination,
    totalDocumentCount,
  ]);

  const clearDocLimitState = useCallback(() => {
    setIsLoading(undefined);
    setIsMaxLimit(undefined);
    setTotalDocumentToView(undefined);
  }, []);

  if (isEmpty(destination) || isEmpty(totalDocumentCount)) {
    return {maxCount, clearDocLimitState};
  }

  return {
    maxCount,
    isMaxLimit,
    totalDocumentToView,
    isLoading,
    clearDocLimitState,
  };
};

// -----------------------------------
// ArrDocMaxLimitWarning Component
// -----------------------------------
const ArrDocMaxLimitWarning = ({
  destination,
  show = false,
  totalDocumentCount,
  onCancel,
  onContinue,
}: ArrDocMaxLimitWarningProps) => {
  const queryClient = useQueryClient();

  const {
    data: limitType,
    refetch: docLimitTypeRequest,
    isLoading: docLimitTypeLoading,
  } = useLongSortLimit(false);

  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [selectedDestination, setSelectedDestination] = useState<
    DocMaxLimitDestination | undefined
  >(undefined);

  const {
    isLoading: isDocLimitLoading,
    maxCount,
    isMaxLimit,
    totalDocumentToView,
    clearDocLimitState,
  } = useDocLimitConfig(selectedDestination, totalDocumentCount);

  useEffect(() => {
    show && setSelectedDestination(destination);
  }, [show, destination]);

  const limitedCount = useMemo(
    () =>
      limitType?.toLowerCase() === 'long'
        ? maxCount
        : `${(maxCount ?? 0) / 1000}K`,
    [limitType, maxCount]
  );

  const isLoading = useMemo(
    () => isDocLimitLoading || docLimitTypeLoading,
    [docLimitTypeLoading, isDocLimitLoading]
  );

  const modelTextPropsConfig = useMemo(
    () => ({
      [SourceTypeCode.DOCVIEW]: {
        title: t('common.arrDocMaxLimitWarning.docView.title'),
        description: t('common.arrDocMaxLimitWarning.docView.description', {
          limitedCount: limitedCount,
        }),
      },
      [DocMaxLimitDestinationCode.DATASETDOCVIEW]: {
        title: t('common.arrDocMaxLimitWarning.docView.title'),
        description: t('common.arrDocMaxLimitWarning.docView.description', {
          limitedCount: limitedCount,
        }),
      },
      [SourceTypeCode.DOCLIST]: {
        title: t('common.arrDocMaxLimitWarning.docList.title'),
        description: t('common.arrDocMaxLimitWarning.docList.description', {
          limitedCount: limitedCount,
        }),
      },
      [DocMaxLimitDestinationCode.DATASETDOCLIST]: {
        title: t('common.arrDocMaxLimitWarning.docList.title'),
        description: t('common.arrDocMaxLimitWarning.docList.description', {
          limitedCount: limitedCount,
        }),
      },
    }),
    [limitedCount]
  );

  const clearAPICache = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [
        'docViewLimit',
        'docListLimit',
        'datasetDocViewLimit',
        'datasetDocListLimit',
        'docLongSortLimit',
      ],
    });
  }, [queryClient]);

  const onButtonClickEvent = useCallback(
    (callback?: (() => void) | ((totalDocsToView: number) => void)) => {
      setShowDialog(false);
      setSelectedDestination(undefined);
      callback?.(totalDocumentToView ?? 0);
      clearAPICache();
      clearDocLimitState?.();
    },
    [clearAPICache, clearDocLimitState, totalDocumentToView]
  );

  const onContinueEvent = useCallback(() => {
    onButtonClickEvent(onContinue);
  }, [onButtonClickEvent, onContinue]);

  const onCancelEvent = useCallback(() => {
    onButtonClickEvent(onCancel);
  }, [onButtonClickEvent, onCancel]);

  const buttons: ActionButton[] = useMemo(
    () => [
      {
        id: '1',
        variant: 'secondary',
        label: t('common.cancel'),
        dataTestId: t('common.cancel'),
        onClick: onCancelEvent,
      },
      {
        id: '2',
        variant: 'primary',
        label: t('common.continue'),
        dataTestId: t('common.continue'),
        onClick: onContinueEvent,
      },
    ],
    [onCancelEvent, onContinueEvent]
  );

  useEffect(() => {
    if (show) {
      docLimitTypeRequest();
    }
  }, [docLimitTypeRequest, show]);

  useEffect(() => {
    if (
      show &&
      !isLoading &&
      !isEmpty(isMaxLimit) &&
      !isEmpty(totalDocumentToView)
    ) {
      if (isMaxLimit) {
        setShowDialog(true);
      } else {
        onContinueEvent();
      }
    }
  }, [isLoading, isMaxLimit, onContinueEvent, show, totalDocumentToView]);

  return (
    <div
      className="ArrDocMaxLimitWarning"
      data-testid={`arr-doc-max-limit-warning-div`}>
      <ArrModal
        title={modelTextPropsConfig?.[destination ?? '']?.title ?? ''}
        description={
          modelTextPropsConfig?.[destination ?? '']?.description ?? ''
        }
        showModal={showDialog}
        showClose={true}
        onClose={onCancelEvent}
        dataTestId="doc-maxlimit-warning"
        footer={<ArrDocMaxLimitWarningFooter actionButtons={buttons} />}
      />
      <ArrLoadingSpinner overlay={true} show={show && isLoading} />
    </div>
  );
};

export default ArrDocMaxLimitWarning;
